import React from "react";
import { Card, CardBody, Row, Col } from "reactstrap";

/**
 * Polished "Coming Soon" card used by module landing pages that don't
 * have functionality wired up yet (Accounting, Shipping, KPI, etc).
 *
 * Replaces the legacy autoplaying video version with a flat, branded
 * card that sits naturally below a DashboardPageHeader. No external
 * media, no inline video tag, no Lottie player.
 *
 * Pure presentation. No state, no fetching.
 *
 * Props
 *  - title:        large heading (default "Coming Soon")
 *  - message:      paragraph below the heading
 *  - icon:         ri-* icon shown in the colored disc
 *  - tone:         "primary" | "info" | "success" | "warning" | "danger" |
 *                  "secondary" — controls the disc + badge color
 *  - eta:          short timeline string (e.g. "Q1 2026") rendered as a chip
 *  - features:     optional array of strings — bullet list of "what's
 *                  planned" so this page still tells the user something
 *  - contact:      optional contact line (e.g. "Reach out to IT for early access")
 *  - actions:      optional React node for buttons (e.g. "Back to dashboard")
 *  - compact:      bool — tighter padding for embedded use (e.g. inside a tab)
 */
const TONE_HEX = {
  primary: "#405189",
  info: "#299cdb",
  success: "#0ab39c",
  warning: "#f7b84b",
  danger: "#f06548",
  secondary: "#74788d",
};

const ComingSoonCard = ({
  title = "Coming Soon",
  message = "This module is being built out. We'll let you know as soon as it's ready.",
  icon = "ri-tools-fill",
  tone = "primary",
  eta,
  features,
  contact,
  actions,
  compact = false,
}) => {
  const hex = TONE_HEX[tone] || TONE_HEX.primary;
  const padY = compact ? "32px 24px" : "56px 24px";
  const discSize = compact ? 64 : 84;

  return (
    <Card
      className="shadow-sm border-0"
      style={{ borderRadius: 16, overflow: "hidden", position: "relative" }}
    >
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          background: hex,
          opacity: 0.7,
        }}
      />
      <CardBody style={{ padding: padY }}>
        <Row className="align-items-center g-4 justify-content-center">
          <Col md={features && features.length ? 5 : 12} className="text-center">
            <div
              className="d-inline-flex align-items-center justify-content-center mb-3"
              style={{
                width: discSize,
                height: discSize,
                borderRadius: "50%",
                background: `${hex}14`,
                color: hex,
              }}
            >
              <i
                className={icon}
                style={{ fontSize: compact ? 30 : 40 }}
                aria-hidden="true"
              />
            </div>

            {eta ? (
              <div className="mb-2">
                <span
                  className="badge fw-medium"
                  style={{
                    background: `${hex}1A`,
                    color: hex,
                    padding: "5px 12px",
                    fontSize: 11,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                  }}
                >
                  <i className="ri-time-line align-bottom me-1" />
                  {eta}
                </span>
              </div>
            ) : null}

            <h4
              className="ff-secondary fw-semibold mb-2"
              style={{ color: "#212529" }}
            >
              {title}
            </h4>

            {message ? (
              <p
                className="text-muted mb-3"
                style={{
                  fontSize: 14,
                  maxWidth: 460,
                  margin: "0 auto 1rem",
                }}
              >
                {message}
              </p>
            ) : null}

            {contact ? (
              <p
                className="text-muted small mb-3"
                style={{ fontSize: 12, maxWidth: 460, margin: "0 auto 1rem" }}
              >
                <i className="ri-mail-line align-bottom me-1" />
                {contact}
              </p>
            ) : null}

            {actions ? (
              <div className="d-flex justify-content-center gap-2 flex-wrap">
                {actions}
              </div>
            ) : null}
          </Col>

          {features && features.length ? (
            <Col md={6} lg={5}>
              <div
                style={{
                  background: "#f8f9fb",
                  borderRadius: 12,
                  padding: "18px 20px",
                  border: "1px solid #eef0f4",
                }}
              >
                <div
                  className="text-uppercase fw-medium mb-2"
                  style={{
                    fontSize: 11,
                    letterSpacing: "0.06em",
                    color: hex,
                  }}
                >
                  What's planned
                </div>
                <ul
                  className="mb-0 list-unstyled"
                  style={{ fontSize: 13, color: "#495057" }}
                >
                  {features.map((f, i) => (
                    <li key={i} className="d-flex align-items-start gap-2 mb-2">
                      <i
                        className="ri-checkbox-circle-line"
                        style={{
                          color: hex,
                          fontSize: 16,
                          marginTop: 1,
                          flexShrink: 0,
                        }}
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Col>
          ) : null}
        </Row>
      </CardBody>
    </Card>
  );
};

export default ComingSoonCard;
